import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * AppointmentCalendar component displays appointments in a calendar view
 */
const AppointmentCalendar = ({ 
  appointments,
  practitioners,
  onViewAppointment,
  onCreateAppointment,
  canManageAppointments
}) => {
  // State for calendar
  const [calendarDays, setCalendarDays] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isLoading, setIsLoading] = useState(false);
  
  // Generate calendar days for the current month
  useEffect(() => {
    setIsLoading(true);
    generateCalendarDays(currentMonth);
    setIsLoading(false);
  }, [currentMonth, appointments]);
  
  /**
   * Generate the days for the calendar view
   */
  const generateCalendarDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    
    // First day of the month
    const firstDay = new Date(year, month, 1);
    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);
    
    // Get the day of the week of the first day (0-6, where 0 is Sunday)
    const firstDayOfWeek = firstDay.getDay();
    // Adjust to have Monday as the first day of the week (0-6, where 0 is Monday)
    const adjustedFirstDayOfWeek = (firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1);
    
    // Calculate days from previous month to display
    const daysFromPreviousMonth = adjustedFirstDayOfWeek;
    const previousMonth = new Date(year, month, 0);
    const previousMonthDays = previousMonth.getDate();
    
    // Calculate days from next month to display (to fill the grid)
    const totalDaysInCurrentMonth = lastDay.getDate();
    const totalCells = Math.ceil((totalDaysInCurrentMonth + daysFromPreviousMonth) / 7) * 7;
    const daysFromNextMonth = totalCells - (totalDaysInCurrentMonth + daysFromPreviousMonth);
    
    // Generate calendar days array
    const days = [];
    
    // Add days from previous month
    for (let i = daysFromPreviousMonth - 1; i >= 0; i--) {
      const dayDate = new Date(year, month - 1, previousMonthDays - i);
      days.push({
        date: dayDate,
        isCurrentMonth: false,
        appointments: filterAppointmentsByDate(appointments, dayDate)
      });
    }
    
    // Add days from current month
    for (let i = 1; i <= totalDaysInCurrentMonth; i++) {
      const dayDate = new Date(year, month, i);
      days.push({
        date: dayDate,
        isCurrentMonth: true,
        isToday: isToday(dayDate),
        appointments: filterAppointmentsByDate(appointments, dayDate)
      });
    }
    
    // Add days from next month
    for (let i = 1; i <= daysFromNextMonth; i++) {
      const dayDate = new Date(year, month + 1, i);
      days.push({
        date: dayDate,
        isCurrentMonth: false,
        appointments: filterAppointmentsByDate(appointments, dayDate)
      });
    }
    
    setCalendarDays(days);
  };
  
  /**
   * Filter appointments for a specific date
   */
  const filterAppointmentsByDate = (appointments, date) => {
    return appointments.filter(appointment => {
      const appointmentDate = new Date(appointment.appointmentTime);
      return (
        appointmentDate.getDate() === date.getDate() &&
        appointmentDate.getMonth() === date.getMonth() &&
        appointmentDate.getFullYear() === date.getFullYear()
      );
    });
  };
  
  /**
   * Check if a date is today
   */
  const isToday = (date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };
  
  /**
   * Navigate to previous month
   */
  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };
  
  /**
   * Navigate to next month
   */
  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };
  
  /**
   * Navigate to current month
   */
  const goToCurrentMonth = () => {
    setCurrentMonth(new Date());
  };
  
  /**
   * Format date for display
   */
  const formatMonthYear = (date) => {
    return date.toLocaleDateString('zh-CN', { year: 'numeric', month: 'long' });
  };
  
  /**
   * Format time for display in appointment cards
   */
  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
  };
  
  /**
   * Get color class for appointment status
   */
  const getAppointmentColorClass = (status) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-100 border-blue-300';
      case 'confirmed': return 'bg-green-100 border-green-300';
      case 'completed': return 'bg-purple-100 border-purple-300';
      case 'cancelled': return 'bg-red-100 border-red-300';
      case 'noshow': return 'bg-gray-100 border-gray-300';
      default: return 'bg-gray-100 border-gray-300';
    }
  };
  
  /**
   * Get practitioner name by ID
   */
  const getPractitionerName = (practitionerId) => {
    const practitioner = practitioners.find(p => p.id === practitionerId);
    return practitioner ? practitioner.name : '未指定调理师';
  };
  
  // Days of the week headers
  const weekdays = ['周一', '周二', '周三', '周四', '周五', '周六', '周日'];
  
  return (
    <div className="bg-white shadow rounded-lg">
      {/* Calendar Header */}
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center">
          <h2 className="text-lg font-semibold text-gray-900">
            {formatMonthYear(currentMonth)}
          </h2>
          <button
            onClick={goToCurrentMonth}
            className="ml-2 p-1 rounded-full text-gray-400 hover:text-gray-500 hover:bg-gray-100"
          >
            <span className="sr-only">回到当月</span>
            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </button>
        </div>
        <div className="flex">
          <button
            onClick={goToPreviousMonth}
            className="p-2 rounded-full text-gray-400 hover:text-gray-500 hover:bg-gray-100"
          >
            <span className="sr-only">上个月</span>
            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={goToNextMonth}
            className="ml-2 p-2 rounded-full text-gray-400 hover:text-gray-500 hover:bg-gray-100"
          >
            <span className="sr-only">下个月</span>
            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
      
      {/* Calendar Grid */}
      <div className="overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
          </div>
        ) : (
          <div className="grid grid-cols-7 border-b border-gray-200">
            {/* Weekday Headers */}
            {weekdays.map((weekday, index) => (
              <div 
                key={index} 
                className="py-2 text-center text-xs font-semibold text-gray-700 border-r last:border-r-0 border-gray-200"
              >
                {weekday}
              </div>
            ))}
            
            {/* Calendar Days */}
            {calendarDays.map((day, index) => (
              <div 
                key={index} 
                className={`min-h-[8rem] border-r last:border-r-0 border-b last:border-b-0 border-gray-200 overflow-hidden ${
                  day.isCurrentMonth ? 'bg-white' : 'bg-gray-50 text-gray-400'
                } ${day.isToday ? 'bg-blue-50' : ''}`}
              >
                <div className="flex flex-col h-full">
                  {/* Day Header */}
                  <div className="flex justify-between items-center p-1">
                    <span className={`text-sm font-medium ${day.isToday ? 'text-blue-600' : ''}`}>
                      {day.date.getDate()}
                    </span>
                    {/* Add appointment button */}
                    {canManageAppointments && day.isCurrentMonth && (
                      <button
                        onClick={() => onCreateAppointment(day.date)}
                        className="p-1 rounded-full text-gray-400 hover:text-indigo-600 hover:bg-gray-100"
                      >
                        <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </button>
                    )}
                  </div>
                  
                  {/* Appointments for the day */}
                  <div className="flex-1 overflow-y-auto p-1 space-y-1">
                    {day.appointments.sort((a, b) => new Date(a.appointmentTime) - new Date(b.appointmentTime)).map((appointment) => (
                      <div
                        key={appointment.id}
                        onClick={() => onViewAppointment(appointment.id)}
                        className={`p-1 text-xs rounded border cursor-pointer ${getAppointmentColorClass(appointment.status)}`}
                      >
                        <div className="font-medium truncate">
                          {formatTime(appointment.appointmentTime)} {appointment.customerName}
                        </div>
                        <div className="truncate">
                          {appointment.treatmentName || '普通调理'} | {getPractitionerName(appointment.practitionerId)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

AppointmentCalendar.propTypes = {
  appointments: PropTypes.array.isRequired,
  practitioners: PropTypes.array.isRequired,
  onViewAppointment: PropTypes.func.isRequired,
  onCreateAppointment: PropTypes.func.isRequired,
  canManageAppointments: PropTypes.bool.isRequired
};

AppointmentCalendar.defaultProps = {
  appointments: [],
  practitioners: []
};

export default AppointmentCalendar;